// External imports
import React, { useState } from "react";
import { AiOutlineLineChart } from "react-icons/ai";

// Internal imports
import { Modal } from "../../../../../../components/core/modal/Modal";
import {
    OrderAdditionalTestModalContainer,
    OrderAdditionalTestModalContentContainer,
    OrderAdditionalTestModalForm,
    OrderAdditionalTestModalContentLabel,
    OrderAdditionalTestModalFormTitle,
    OrderAdditionalTestModalContentDescription,
    OrderAdditionalTestModalContentWrapper,
    OrderAdditionalTestModalFormFooter,
    OrderAdditionalTestModalFlexContainer,
} from "./OrderAdditionalTestReport.styles";
import { MdOutlineClose } from "react-icons/md";
import TurnTablet from "../../../../notePage/components/TurnTablet/TurnTablet";
import TextArea from "../../../../../../components/core/input-form/TextArea";
import WebsiteButton from "../../../../../../components/core/button/WebsiteButton";
import IconActionButton from "../../../../../../components/core/icon-action-button/IconActionButton";
import { formatGameTime } from "../../../../../../core/gameTime/formatTime";
import { turnsToTime } from "../../../../../../core/LocalStorage/turnLogic/core";
// Translation
import { AdditionalTestTranslations } from "../../../translations/types";
import { useLanguage } from "../../../../../../core/translations/LanguageContext";
import { enTranslations } from "../../../translations/en";
import { deTranslations } from "../../../translations/de";
import { SupportedLanguage } from "../../../../../../core/translations/supportedLanguages";

// set the translations for the page
const TRANSLATIONS: Record<SupportedLanguage, AdditionalTestTranslations> = {
    en: enTranslations,
    de: deTranslations,
};
/**
 * OrderAdditionalTestReportProps
 *
 * This interface is for the props of the OrderAdditionalTestReport component.
 */
interface OrderAdditionalTestReportProps {
    title: string;
    details: string;
    notes?: string;
    allowNotes?: boolean;
    turn_ordered: number;
    onClose: () => void;
    reason?: string;
    summary?: string;
    onNoteSave?: any;
}

/**
 * OrderAdditionalTestReportModal
 *
 * This component is a modal for the investigation report. This is important because it allows
 * the user to view a report or image, and add notes to the report. The notes end up in the
 * documentation.
 */
export const OrderAdditionalTestReportModal = ({
                                                   title,
                                                   details,
                                                   allowNotes,
                                                   notes,
                                                   turn_ordered,
                                                   onClose,
                                                   reason,
                                                   summary,
                                                   onNoteSave,
                                               }: OrderAdditionalTestReportProps) => {
    const [note, setNote] = useState(notes ?? "");
    const { language } = useLanguage();
    const translations = TRANSLATIONS[language];

    const handleSubmit = async () => {
        try {
            if (onNoteSave) {
                await onNoteSave(note);
                onClose();
            }
        } catch (err) {
            console.error("Failed to save note:", err);
        }
    };

    const isPlaceholder = (value?: string) => !value || value === "XXXXXXXXXX";

    return (
        <>
            <Modal onRequestClose={onClose}>
                <OrderAdditionalTestModalContainer>
                    <MdOutlineClose onClick={onClose} />
                    <OrderAdditionalTestModalFormTitle>
                        {translations.reportFor} {translations[title]}
                        <TurnTablet
                            turn={formatGameTime(turnsToTime(turn_ordered))}
                        />
                    </OrderAdditionalTestModalFormTitle>
                    <OrderAdditionalTestModalContentContainer>
                        <OrderAdditionalTestModalForm
                            onSubmit={(e: React.ChangeEvent<HTMLFormElement>) =>
                                e.preventDefault()
                            }
                        >
                            {!isPlaceholder(summary) && (
                                <OrderAdditionalTestModalContentWrapper>
                                    <OrderAdditionalTestModalContentLabel>
                                        {translations.summary}:
                                    </OrderAdditionalTestModalContentLabel>
                                    <OrderAdditionalTestModalContentDescription>
                                        {summary}
                                    </OrderAdditionalTestModalContentDescription>
                                </OrderAdditionalTestModalContentWrapper>
                            )}

                            {!isPlaceholder(reason) && (
                                <OrderAdditionalTestModalContentWrapper>
                                    <OrderAdditionalTestModalContentLabel>
                                        {translations.reasonReferral}:
                                    </OrderAdditionalTestModalContentLabel>
                                    <OrderAdditionalTestModalContentDescription>
                                        {reason}
                                    </OrderAdditionalTestModalContentDescription>
                                </OrderAdditionalTestModalContentWrapper>
                            )}

                            <OrderAdditionalTestModalContentWrapper>
                                <OrderAdditionalTestModalFlexContainer>
                                    <OrderAdditionalTestModalContentLabel>
                                        {translations.report}:
                                    </OrderAdditionalTestModalContentLabel>
                                    <OrderAdditionalTestModalContentDescription>
                                        {details || translations.noFindings}
                                    </OrderAdditionalTestModalContentDescription>
                                </OrderAdditionalTestModalFlexContainer>
                            </OrderAdditionalTestModalContentWrapper>

                            <OrderAdditionalTestModalContentWrapper>
                                <OrderAdditionalTestModalContentLabel>
                                    {translations.notes}:
                                </OrderAdditionalTestModalContentLabel>

                                {allowNotes ? (
                                    notes ? (
                                        <OrderAdditionalTestModalContentDescription>
                                            {notes}
                                        </OrderAdditionalTestModalContentDescription>
                                    ) : (
                                        <TextArea
                                            autoFocus
                                            rows={3}
                                            placeholder={translations.notesPlaceholder}
                                            value={note}
                                            onChange={(
                                                e: React.ChangeEvent<HTMLTextAreaElement>
                                            ) => setNote(e.target.value)}
                                        />
                                    )
                                ) : (
                                    <OrderAdditionalTestModalContentDescription>
                                        {translations.noNotes}
                                    </OrderAdditionalTestModalContentDescription>
                                )}
                            </OrderAdditionalTestModalContentWrapper>

                            <OrderAdditionalTestModalFormFooter>
                                <WebsiteButton
                                    type="submit"
                                    tag="button"
                                    disabled={!note.trim()}
                                    onClick={handleSubmit}
                                >
                                    {note.length > 0 ? translations.submitButton : translations.reviewedText}
                                </WebsiteButton>
                            </OrderAdditionalTestModalFormFooter>
                        </OrderAdditionalTestModalForm>
                    </OrderAdditionalTestModalContentContainer>
                </OrderAdditionalTestModalContainer>
            </Modal>
        </>
    );
};

export default OrderAdditionalTestReportModal;
